import React, { FunctionComponent, ReactChild, useContext, useEffect, useMemo, useState } from 'react'
import { Dimensions, useColorScheme } from 'react-native'

import { debounce } from 'lodash-es'
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components/native'

import {
  BreakpointToAssetNameMap,
  getProductTypeMetadata,
  PRODUCT_TYPE,
  ResponsiveBreakpoints,
  spacing,
  Spacing,
  Theme,
  ThemeAssets,
  ThemeColors,
  ThemeMetadata,
} from '@lyrahealth-inc/shared-app-logic'

import { breakpoints as breakpointsConstants } from '../../constants'
import { useThemeSettings } from '../../hooks/useThemeSettings'
import { ProductTypeContext } from '../productType/productTypeContext'

export interface ThemeType {
  breakpoints: ResponsiveBreakpoints
  colors: ThemeColors
  assets: ThemeAssets
  spacing: Spacing
  themeType: Theme
  setThemeType: (theme: Theme) => void
  isDarkMode: boolean
  isLyraSelect: boolean
}

function calculateBreakpoints(width: number, height: number): ResponsiveBreakpoints {
  const { mobileXs, mobileXsHeight, mobileS, mobileM, mobileL, tablet, laptop, desktop, desktopL, mobileXxs } =
    breakpointsConstants
  return {
    isMinWidthMobileXxs: width >= mobileXxs,
    isMinWidthMobileXs: width >= mobileXs,
    isMinHeightMobileXs: height >= mobileXsHeight,
    isMinWidthMobileS: width >= mobileS,
    isMinWidthMobileM: width >= mobileM,
    isMinWidthMobileL: width >= mobileL,
    isMinWidthTablet: width >= tablet,
    isMinWidthLaptop: width >= laptop,
    isMinWidthDesktop: width >= desktop,
    isMinWidthDesktopL: width >= desktopL,
    isMobileSized: width < tablet,
  }
}

/** Require and return the asset module for the given product type */
type AssetModule = Record<string, string>
const requireAssetModule = (productType: PRODUCT_TYPE): AssetModule => {
  let module: AssetModule
  const isLyraSelect = productType === PRODUCT_TYPE.SELECT
  try {
    module = isLyraSelect
      ? require('@lyrahealth-inc/ui-core-crossplatform/src/assets/lyraSelect')
      : require('@lyrahealth-inc/ui-core-crossplatform/src/assets/wfmh')
  } catch (e) {
    console.debug(`Error loading asset modules via absolute paths; fall back to relative imports: ${e.message}`)
    module = isLyraSelect ? require('../../assets/lyraSelect') : require('../../assets/wfmh')
  }
  return module
}

/**
 * Resolve and map asset names to their actual references from the asset module.
 * Update and return the given assets map with the resolved asset paths.
 */
function resolveAssetPaths(themeAssets: ThemeAssets, productType: PRODUCT_TYPE): ThemeAssets {
  const assetModule = requireAssetModule(productType)
  const assets: ThemeAssets = {}
  Object.entries(themeAssets).forEach(([key, assetConfig]) => {
    const parsedAssetNames: BreakpointToAssetNameMap = { defaultAsset: '' }
    Object.entries(assetConfig.assetNames).forEach(([breakpoint, assetName]) => {
      try {
        const asset = assetModule[assetName as string]
        parsedAssetNames[breakpoint as keyof BreakpointToAssetNameMap] = asset || assetName
      } catch (error) {
        console.warn(`Failed to load asset: ${assetName}`, error)
        parsedAssetNames[breakpoint as keyof BreakpointToAssetNameMap] = assetName
      }
    })
    assets[key] = { ...assetConfig, assetNames: parsedAssetNames }
  })
  return assets
}

export const ThemeProvider: FunctionComponent<{
  children: ReactChild | ReactChild[]
  /**
   * Not needed in App.tsx but needed for Storybook to use the Theme selector
   * in libs/ui-core-crossplatform/.storybook/decorators.js
   */
  theme?: Theme
}> = ({ children, theme: providedTheme }) => {
  const { themeType, setThemeType } = useThemeSettings()
  const { isLyraSelect, productType } = useContext(ProductTypeContext)

  const [breakpoints, setBreakpoints] = useState(
    calculateBreakpoints(Dimensions.get('window').width, Dimensions.get('window').height),
  )

  const colorScheme = useColorScheme()

  useEffect(() => {
    const listener = Dimensions.addEventListener(
      'change',
      debounce(({ window }) => {
        setBreakpoints(calculateBreakpoints(window.width, window.height))
      }, 75),
    )
    return () => listener.remove()
  })

  const isDarkMode = useMemo(() => {
    if (providedTheme) {
      return providedTheme === Theme.DARK
    }

    return themeType === Theme.DARK || (themeType === Theme.SYSTEM && colorScheme === Theme.DARK)
  }, [colorScheme, providedTheme, themeType])

  const colorTheme = isDarkMode ? Theme.DARK : Theme.LIGHT

  const { colors, assets } = useMemo(() => {
    const { colorTokens, assets: themeAssets } = getProductTypeMetadata(colorTheme, productType) as ThemeMetadata
    return {
      colors: colorTokens,
      assets: resolveAssetPaths(themeAssets, productType),
    }
  }, [colorTheme, productType])

  return (
    <StyledComponentsThemeProvider
      theme={{
        breakpoints,
        colors,
        assets,
        spacing,
        themeType,
        setThemeType,
        isDarkMode,
        isLyraSelect,
      }}
    >
      {children}
    </StyledComponentsThemeProvider>
  )
}
